import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  InputBase,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Folder as FolderIcon,
  ConfirmationNumber as TicketIcon,
  Comment as CommentIcon,
  Description as DocumentIcon,
  Domain as DomainIcon,
  Help as HelpIcon,
  ReceiptLong as PurchaseOrderIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';
import SherlockIcon, { SHERLOCK_GREETING } from './SherlockIcon';
import { getChrome } from '../../theme/chrome';

interface SearchResult {
  id: string;
  type: 'client' | 'project' | 'task' | 'task_template' | 'ticket' | 'user' | 'comment' | 'document' | 'company' | 'article' | 'purchase_order' | 'department';
  title: string;
  subtitle?: string;
  description?: string;
  metadata?: string;
  status?: string;
}

interface GlobalSearchProps {
  themeMode?: string;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ themeMode }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false); // mobile expanded state
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const debouncedSearch = useDebounce(searchTerm, 300);

  // Match the main content / drawer backgrounds per theme so the expanded
  // mobile search panel doesn't look like a stray overlay.
  const chrome = getChrome(themeMode ?? 'light');
  const themedBg = chrome.mainBg === 'inherit' ? theme.palette.background.paper : chrome.mainBg;
  // Any light-mode palette has a light main surface, so it needs dark text.
  const onLightMain = theme.palette.mode === 'light';
  const themedFg = onLightMain ? theme.palette.text.primary : '#ffffff';
  const themedPlaceholder = onLightMain ? alpha(theme.palette.text.primary, 0.6) : alpha('#ffffff', 0.7);

  // Fetch search results
  const { data: results, isLoading } = useQuery({
    queryKey: ['global-search', debouncedSearch],
    queryFn: async () => {
      if (!debouncedSearch || debouncedSearch.length < 2) return [];
      const response = await api.get('/search', {
        params: { q: debouncedSearch }
      });
      return response.data.data;
    },
    enabled: debouncedSearch.length >= 2,
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (isMobile) {
          setIsExpanded(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile]);

  // Focus input when expanding on mobile
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  const getIcon = (type: string) => {
    switch (type) {
      case 'client':
        return <BusinessIcon />;
      case 'project':
        return <FolderIcon />;
      case 'task':
        return <AssignmentIcon />;
      case 'task_template':
        return <AssignmentIcon />;
      case 'ticket':
        return <TicketIcon />;
      case 'user':
        return <PersonIcon />;
      case 'comment':
        return <CommentIcon />;
      case 'document':
        return <DocumentIcon />;
      case 'company':
        return <DomainIcon />;
      case 'article':
        return <HelpIcon />;
      case 'purchase_order':
        return <PurchaseOrderIcon />;
      case 'department':
        return <GroupsIcon />;
      default:
        return <SearchIcon />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'client':
        return 'primary';
      case 'project':
        return 'secondary';
      case 'task':
        return 'info';
      case 'task_template':
        return 'info';
      case 'ticket':
        return 'warning';
      case 'user':
        return 'success';
      case 'comment':
        return 'default';
      case 'document':
        return 'default';
      case 'company':
        return 'primary';
      case 'article':
        return 'info';
      case 'purchase_order':
        return 'secondary';
      case 'department':
        return 'success';
      default:
        return 'default';
    }
  };

  /**
   * Map entity status values to MUI Chip colors.
   * Completed/closed/inactive → muted; active/open → vibrant.
   */
  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    const s = status.toLowerCase();
    if (['completed', 'done', 'closed', 'resolved'].includes(s)) return 'success';
    if (['cancelled', 'inactive', 'rejected'].includes(s)) return 'error';
    if (['in_progress', 'in progress', 'active', 'open'].includes(s)) return 'info';
    if (['todo', 'pending', 'on_hold', 'on hold', 'planning'].includes(s)) return 'warning';
    return 'default';
  };

  const handleResultClick = (result: SearchResult) => {
    setIsOpen(false);
    setSearchTerm('');

    // Navigate based on type
    switch (result.type) {
      case 'client':
        navigate(`/clients/${result.id}`);
        break;
      case 'project':
        navigate(`/projects/${result.id}`);
        break;
      case 'task':
        navigate(`/tasks/${result.id}`);
        break;
      case 'task_template':
        // The /task-templates/:id edit route is crm:manage-gated; the list is
        // open to every employee, so land there (works for all who see the hit).
        navigate('/task-templates');
        break;
      case 'ticket':
        navigate(`/tickets/${result.id}`);
        break;
      case 'user':
        navigate(`/users/${result.id}`);
        break;
      case 'company':
        navigate(`/companies/${result.id}`);
        break;
      case 'article':
        navigate(`/knowledge-base/${result.id}`);
        break;
      case 'purchase_order':
        // No per-PO detail route; the component-gated list is where a PO is
        // opened. Hand the id over so the list expands + scrolls to it (and
        // clears its default last-7-days filter so older POs are visible).
        navigate('/inventory/purchase-orders', { state: { expandPOId: result.id } });
        break;
      case 'department':
        // No per-department detail route; departments live on the list page.
        navigate('/departments');
        break;
      case 'comment':
        // Navigate to parent entity (metadata contains parent info)
        if (result.metadata) {
          const [parentType, parentId] = result.metadata.split(':');
          navigate(`/${parentType}s/${parentId}`);
        }
        break;
      case 'document':
        // Navigate to parent entity
        if (result.metadata) {
          const [parentType, parentId] = result.metadata.split(':');
          navigate(`/${parentType}s/${parentId}`);
        }
        break;
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <Box ref={searchRef} sx={{ position: 'relative', width: isMobile ? 'auto' : { xs: 200, sm: 300, md: 400 } }}>
      {isMobile ? (
        <>
          {!isExpanded && (
            <IconButton
              aria-label="open search"
              onClick={() => setIsExpanded(true)}
              size="small"
              sx={{ color: alpha('#ff8c00', 0.9) }}
            >
              <SherlockIcon ariaLabel="open search" tooltip />
            </IconButton>
          )}
          {isExpanded && (
            <Box
              sx={{
                position: 'fixed',
                top: 8,
                left: 8,
                right: 8,
                zIndex: 1400,
              }}
            >
              <Paper
                elevation={8}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: themedBg,
                  border: `1px solid ${alpha('#ff8c00', 0.5)}`,
                  borderRadius: 2,
                  px: 1,
                  py: 0.5,
                }}
              >
                <IconButton size="small" onClick={() => { setIsExpanded(false); setIsOpen(false); }} sx={{ color: alpha('#ff8c00', 0.9), mr: 0.5 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
                <SherlockIcon sx={{ mr: 1 }} />
                <InputBase
                  inputRef={inputRef}
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setIsOpen(true);
                  }}
                  onFocus={() => setIsOpen(true)}
                  sx={{
                    flex: 1,
                    fontSize: '0.95rem',
                    color: themedFg,
                    '& input::placeholder': {
                      color: themedPlaceholder,
                      opacity: 1
                    }
                  }}
                />
                {searchTerm && (
                  <IconButton size="small" onClick={handleClear} sx={{ color: alpha('#ff8c00', 0.9) }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Paper>

              {/* Results on mobile overlay */}
              {isOpen && (
                <Paper
                  elevation={8}
                  sx={{
                    mt: 1,
                    maxHeight: 500,
                    overflow: 'auto',
                    borderRadius: 2,
                    backgroundColor: themedBg,
                    color: themedFg,
                  }}
                >
                  {isLoading ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Searching...
                      </Typography>
                    </Box>
                  ) : searchTerm.length >= 2 && results && results.length > 0 ? (
                    <List sx={{ py: 0 }}>
                      {results.map((result: SearchResult, index: number) => (
                        <React.Fragment key={`${result.type}-${result.id}`}>
                          {index > 0 && <Divider />}
                          <ListItem disablePadding>
                            <ListItemButton
                              onClick={() => handleResultClick(result)}
                              sx={{
                                py: 1.25,
                                '&:hover': {
                                  backgroundColor: alpha('#ff8c00', 0.08),
                                },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                {getIcon(result.type)}
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <Typography variant="body2" fontWeight="medium">
                                      {result.title}
                                    </Typography>
                                    <Chip
                                      label={result.type}
                                      size="small"
                                      color={getTypeColor(result.type) as any}
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                    {result.status && (
                                      <Chip
                                        label={result.status.replace(/_/g, ' ')}
                                        size="small"
                                        variant="outlined"
                                        color={getStatusColor(result.status)}
                                        sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }}
                                      />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <Box>
                                    {result.subtitle && (
                                      <Typography variant="caption" display="block" color="text.secondary">
                                        {result.subtitle}
                                      </Typography>
                                    )}
                                    {result.description && (
                                      <Typography
                                        variant="caption"
                                        display="block"
                                        color="text.secondary"
                                        sx={{
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                        }}
                                      >
                                        {result.description}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItemButton>
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  ) : searchTerm.length > 0 && searchTerm.length < 2 ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Type at least 2 characters to search
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <SherlockIcon sx={{ fontSize: 48, mb: 1 }} />
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ fontStyle: 'italic', mb: 0.75 }}
                      >
                        “{SHERLOCK_GREETING}”
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Start typing to search
                      </Typography>
                    </Box>
                  )}
                </Paper>
              )}
            </Box>
          )}
        </>
      ) : (
        <>
          {/* Desktop search input */}
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: alpha('#ff8c00', 0.1),
              border: `1px solid ${alpha('#ff8c00', 0.3)}`,
              borderRadius: 2,
              px: 2,
              py: 0.5,
              '&:focus-within': {
                backgroundColor: alpha('#ff8c00', 0.15),
                border: `1px solid ${alpha('#ff8c00', 0.5)}`,
                boxShadow: `0 0 0 2px ${alpha('#ff8c00', 0.1)}`,
              },
            }}
          >
            <SherlockIcon sx={{ mr: 1 }} tooltip />
            <InputBase
              placeholder="Find everything..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              sx={{
                flex: 1,
                fontSize: '0.875rem',
                color: '#ffffff',
                '& input::placeholder': {
                  color: alpha('#ffffff', 0.6),
                  opacity: 1
                }
              }}
            />
            {searchTerm && (
              <IconButton size="small" onClick={handleClear} sx={{ color: alpha('#ff8c00', 0.8) }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
            {isLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </Paper>

          {/* Desktop dropdown */}
          {isOpen && searchTerm.length >= 2 && (
            <Paper
              elevation={8}
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                mt: 1,
                maxHeight: 500,
                overflow: 'auto',
                zIndex: 1300,
                borderRadius: 2,
              }}
            >
              {isLoading ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Searching...
                  </Typography>
                </Box>
              ) : results && results.length > 0 ? (
                <List sx={{ py: 0 }}>
                  {results.map((result: SearchResult, index: number) => (
                    <React.Fragment key={`${result.type}-${result.id}`}>
                      {index > 0 && <Divider />}
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => handleResultClick(result)}
                          sx={{
                            py: 1.5,
                            '&:hover': {
                              backgroundColor: alpha('#ff8c00', 0.08),
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            {getIcon(result.type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <Typography variant="body2" fontWeight="medium">
                                  {result.title}
                                </Typography>
                                <Chip
                                  label={result.type}
                                  size="small"
                                  color={getTypeColor(result.type) as any}
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                                {result.status && (
                                  <Chip
                                    label={result.status.replace(/_/g, ' ')}
                                    size="small"
                                    variant="outlined"
                                    color={getStatusColor(result.status)}
                                    sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                {result.subtitle && (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {result.subtitle}
                                  </Typography>
                                )}
                                {result.description && (
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="text.secondary"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                    }}
                                  >
                                    {result.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <SherlockIcon sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No results found for "{searchTerm}"
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Try different keywords
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* Desktop - minimum characters message */}
          {isOpen && searchTerm.length > 0 && searchTerm.length < 2 && (
            <Paper
              elevation={4}
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                mt: 1,
                p: 2,
                zIndex: 1300,
                borderRadius: 2,
              }}
            >
              <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
                Type at least 2 characters to search
              </Typography>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default GlobalSearch;

